import { cva } from "class-variance-authority";
import { AlertTriangle, Check, ChevronDown, ChevronRight } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetAdHocReportStatus } from "~/api/chat/chat";
import type { AdHocJobStatus } from "~/api/model";
import Loader from "~/assets/Loader.svg";
import { ContentCopyButton } from "~/components/ContentCopyButton";
import { HighlightedMarkdown } from "~/components/HighlightedMarkdown";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "~/components/ui/collapsible";
import { useChatDocumentParams } from "~/helpers";
import { cn } from "~/lib/utils";
import type { TermMatcher } from "~/utils/termMatcher";
import { injectBatesLabels, normalizeCitationMarkers } from "../helpers";
import type { Hint } from "../types";

type ExtractionStatus = AdHocJobStatus["status"];

const containerVariants = cva(
  "my-2 rounded-lg border border-l-4 bg-background p-3",
  {
    variants: {
      status: {
        pending: "border-l-blue-400 border-border",
        processing: "border-l-blue-400 border-border",
        completed: "border-l-green-500 border-border",
        failed: "border-l-red-400 border-border",
      },
    },
    defaultVariants: {
      status: "pending",
    },
  }
);

const isTerminal = (status: ExtractionStatus | undefined): boolean =>
  status === "completed" || status === "failed";

interface AsyncReportBlockProps {
  jobId: string;
  terms?: Hint[];
  medicationMatcher?: TermMatcher | null;
}

export const AsyncReportBlock: FC<AsyncReportBlockProps> = ({
  jobId,
  terms,
  medicationMatcher,
}) => {
  const { organizationId, caseId, chatId } = useChatDocumentParams();

  const { data, isError } = useGetAdHocReportStatus(
    organizationId,
    caseId,
    chatId,
    jobId,
    {
      query: {
        enabled: !!jobId,
        refetchInterval: (query) => {
          if (query.state.status === "error") return false;
          if (isTerminal(query.state.data?.status)) return false;
          return 3000;
        },
        refetchIntervalInBackground: true,
      },
    }
  );

  const effectiveData: AdHocJobStatus | undefined = isError
    ? { status: "failed", errorReason: "" }
    : data;

  return (
    <AsyncReportBlockView
      data={effectiveData}
      terms={terms}
      medicationMatcher={medicationMatcher}
    />
  );
};

interface AsyncReportBlockViewProps {
  data: AdHocJobStatus | undefined;
  terms?: Hint[];
  medicationMatcher?: TermMatcher | null;
}

export const AsyncReportBlockView: FC<AsyncReportBlockViewProps> = ({
  data,
  terms,
  medicationMatcher,
}) => {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(true);

  const status: ExtractionStatus = data?.status ?? "pending";
  const stage =
    data && (data.status === "pending" || data.status === "processing")
      ? data.stage
      : undefined;
  const content = data?.status === "completed" ? data.result.content : null;
  const errorReason = data?.status === "failed" ? data.errorReason : null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className={cn(containerVariants({ status }), "group")}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <StatusIcon status={status} />
              <span className="text-sm font-medium text-foreground">
                <FormattedMessage id="chat.asyncReport.title" />
              </span>
            </div>
            {isOpen ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="mt-3">
            {(status === "pending" || status === "processing") && (
              <p className="text-sm text-muted-foreground">
                {stage ||
                  formatMessage({
                    id:
                      status === "pending"
                        ? "chat.asyncReport.pending"
                        : "chat.asyncReport.processing",
                  })}
              </p>
            )}

            {status === "completed" && content && (
              <>
                <HighlightedMarkdown
                  className="leading-5"
                  terms={terms ?? []}
                  medicationMatcher={medicationMatcher ?? null}
                >
                  {injectBatesLabels(normalizeCitationMarkers(content))}
                </HighlightedMarkdown>
                <div className="h-6 mt-2 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200">
                  <div className="inline-flex items-center gap-2">
                    <ContentCopyButton
                      content={content}
                      preCopyMessageId="copy"
                      color="muted-foreground"
                      eventName="app/chat/async_report_copy.button/click"
                    />
                  </div>
                </div>
              </>
            )}

            {status === "failed" && (
              <p className="text-sm text-muted-foreground">
                {errorReason ||
                  formatMessage({ id: "chat.asyncReport.failed" })}
              </p>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

const StatusIcon: FC<{ status: ExtractionStatus }> = ({ status }) => {
  if (status === "completed") {
    return <Check className="h-4 w-4 text-green-600" />;
  }
  if (status === "failed") {
    return <AlertTriangle className="h-4 w-4 text-red-500" />;
  }
  return (
    <img src={Loader} alt="" height={16} width={16} className="dark:invert" />
  );
};
